import { useState } from "react";
import {
  UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";
import { minutes } from "@/utils";
interface Exchange {
  id: string;
  name: string;
  exchangename?: string;
  type: string;
  logo: string;
  image_url?: string;
  thumb: string;
  thumbnail_url?: string;
  keyRequired: boolean;
  secretRequired: boolean;
  additionalFields: string[];
  api_required_fields?: string[];
  api_active?: boolean;
  csv_active?: boolean;
  tag?: string;
  api_instructions?: string;
  csv_instructions?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface UseGetExchangesProps {
  initialSearchQuery?: string;
  cursor?: string | null;
  limit?: number;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
}

export const useExchangesQueryKey = ["exchanges"] as const;

/**
 * Hook to get exchange info for moonscape to:
 * - populate the exchange dropdown
 * - figure out what fields are needed for each exchange
 * - get images for each exchange
 * - search exchanges by name (with pagination)
 */
export const useGetExchanges = ({
  initialSearchQuery,
  cursor = null,
  limit = 20,
  refetchOnWindowFocus = true,
  staleTime = 1 * minutes,
}: UseGetExchangesProps = {}) => {
  const { user, isAuthenticated, getToken } = useAuth();
  const email = user?.email;
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);

  const getExchanges = async (): Promise<
    PaginatedResponse<Exchange> | Exchange[]
  > => {
    try {
      const token = await getToken();

      // Use search endpoint if search query is provided
      if (searchQuery) {
        const params = { search, cursor, limit };
        const res = await authorizedAxios(token).get("/exchanges/search", {
          params,
        });
        return {
          data: res.data.items,
          nextCursor: res.data.cursor,
          hasMore: res?.data?.items?.remaining > 0,
        };
      }

      // Otherwise use the regular endpoint
      const res = await authorizedAxios(token).get("/exchanges");
      return res.data;
    } catch (error) {
      console.log("[useGetExchanges] error", error);
      throw error;
    }
  };

  const queryKey = [...useExchangesQueryKey];
  if (initialSearchQuery) {
    queryKey.push({ searchQuery, cursor, limit });
  }

  const {
    isLoading: isQueryLoading,
    isIdle,
    isSuccess,
    error,
    isError,
    data,
    refetch,
  }: UseQueryResult<
    PaginatedResponse<Exchange> | Exchange[],
    unknown
  > = useQuery({
    queryKey,
    queryFn: getExchanges,
    enabled: !!email,
    refetchOnWindowFocus,
    staleTime,
    // Keep previous data while fetching new data
    keepPreviousData: true,
  });

  // Helper to handle both paginated and non-paginated responses
  const exchanges = Array.isArray(data) ? data : data?.data ?? [];
  const nextCursor = Array.isArray(data) ? null : data?.nextCursor;
  const hasMore = Array.isArray(data) ? false : data?.hasMore ?? false;

  return {
    isQueryLoading,
    isIdle,
    error,
    exchanges,
    nextCursor,
    hasMore,
    refetch,
    setSearchQuery,
  };
};
